import $ from 'jquery';
import 'jquery-ui/ui/widgets/draggable';
import 'jquery-ui/ui/widgets/resizable';
import {bringToFrontWndStack, getTopMostElement, MenuSource, SearchableMenu} from './DomMenu';
import { PluginHelper } from './Plugin';
import {
    signal,
    arraySignal,
    Fn
} from './lib/signal';
import {CallableDisposableStack, StackDisposable} from "./lib/disposable.js";
import {clamp, ellipsize, now} from './util/CoreUtils';
import {dragdrop1d, EL, hide, isVisible, keyname, preprocessText, renderIcon2, show} from './util/DomUtils';
import {UiContext} from "./util/UiContext";
import {HookCallback} from "./lib/hook";
import {PropertyObject} from "./lib";
import {GoAwayError, IActivityHost, NavigationOptions, OnNavigateOptions} from "./ui/Activity";
import {IHierarchy} from "./lib/hierarchy";
import {ActivityHolder, isSamePageUrl} from "./ui/ActivityHolder";


//var domWindowZIndex=200000;

export type DomWindowBounds = { x: number, y: number, xx: number, yy: number, collapsed?: boolean, hidden?: boolean, url?: string };
export type MobileModeBounds = { collapsed: boolean, hidden?: boolean, url?: string };

export class DomWindowConfig {
    public static isMobileMode: boolean;
    static set(config: { isMobileMode : boolean }) {
        DomWindowConfig.isMobileMode = config.isMobileMode;
    }
    static resetWindowBounds() {
        for(var i = localStorage.length - 1; i >= 0; i--) 
            if (localStorage.key(i).startsWith("winPos_")) 
                localStorage.removeItem(localStorage.key(i));
    }
}
var globIdCounter = 1;
function getNextId() {
    return `tmpwnd-${now().toString(36)}-${(++globIdCounter).toString(36)}${Math.random().toString(36).substring(2, 8)}`;
}

@PluginHelper.Mixin @StackDisposable.Mixin
export class DomWindow implements StackDisposable, PluginHelper, IHierarchy {
    declare [Symbol.dispose]: CallableDisposableStack;
    declare dtor: DisposableStack;

    collapsed:boolean = false;
    origHeight:number = 0;
    url?: URL;
    container: HTMLDivElement;
    readonly titleEl: HTMLDivElement;
    readonly titleTextEl: HTMLElement;
    private readonly titleSpacerEl: HTMLElement;
    closeButton: HTMLButtonElement;
    readonly contentEl: HTMLDivElement;
    readonly footerEl: HTMLDivElement;
    props: PropertyObject = {};
    static coverEl?: HTMLDivElement;
    static protocol : string | null = null;
    hotkeys: {[keyname: string]: Function} = {};
    buttonHotkeys: {[keyname: string]: HTMLButtonElement} = {};
    get $wnd () { return $(this.container); }
    get $content () { return $(this.contentEl); }
    get $footer () { return $(this.footerEl); }
    get $title () { return $(this.titleEl); }
    get hidden() { return this.container.style.display === 'none'; }
    get hideOnClose(): boolean { return this._hideOnClose; }
    set hideOnClose(val: boolean) {
        this._hideOnClose = val;
        this.closeButton.innerText = val ? '—' : 'X';
    }
    private _hideOnClose: boolean = false;
    title: string;
    _uiInitialized = false;
    protected readonly context: UiContext;
    private _windowZoom = 1;

    static mobileContainer : HTMLDivElement;
    static windows : { [key:string] : DomWindow } = {};
    static windowTypes = arraySignal<() => (typeof DomWindow | { name: string, protocol?: string, getConstructor(): Promise<typeof DomWindow> })[]>();
    static windowCreated = signal<(instance: DomWindow) => void>();
    static windowClosed = signal<(instance: DomWindow) => void>();
    private static _current : DomWindow = undefined;
    public static get current() : DomWindow | undefined {
        return DomWindow._current;
    }
    public static set current(v : DomWindow) {
        if (DomWindow._current === v) return;
        if (DomWindow._current) {
            DomWindow._current.container.classList.remove("focused");
        }
        DomWindow._current = v;
        if (v)
            v.container.classList.add("focused");
        else
            DomWindow.restoreFocus();
    }
    public static restoreFocus() {
        const topMost = getTopMostElement("body > .windowOuterDiv.show");
        try {
            DomWindow.forElement(topMost).focus();
        } catch {}
    }

    static reopenTempWindows() {
        for(let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key.startsWith('winPos_tmpwnd-') && !(key.substring(7) in DomWindow.windows)) {
                const data = JSON.parse(localStorage.getItem(key));
                if (data.url && !data.hidden) {
                    DomWindow.navigate(data.url, { id: key.substring(7) });
                }
            }
        }
    }
    static getWindowSwitcherMenuSource(activatingWindow?: DomWindow, e?: MouseEvent): MenuSource {
        return {
            placeholder: 'Go to window',
            static: async () => {
                const menu: any[] = [];

                if (activatingWindow) {
                    for (const btn of activatingWindow.buttonsMenu) {
                        menu.push({
                            text: btn.innerText,
                            hotkey: btn.getAttribute('data-hotkey'),
                            action() {
                                btn.click();
                            }
                        })
                    }
                    if (DomWindowConfig.isMobileMode) {
                        if (activatingWindow.isMobileFullscreen())
                            menu.push({
                                icon: "check_box.png", text: "Fullscreen", secondaryText: null, action() {
                                    activatingWindow.setMobileFullscreen(false);
                                }
                            });
                        else
                            menu.push({
                                icon: "check_box_uncheck.png", text: "Fullscreen", secondaryText: null, action() {
                                    activatingWindow.setMobileFullscreen(true);
                                },
                            });
                    }
                    if (activatingWindow.url) {
                        menu.push(
                            {
                                text: "copy URL", secondaryText: ellipsize(`=${activatingWindow.url}`, 50), action() {
                                    UiContext.instance.toast('info', 'Copied URL: ' + activatingWindow.url.toString(), 3000);
                                    navigator.clipboard.writeText(activatingWindow.url.toString());
                                }
                            },
                        );
                    }
                }
                if (UiContext.instance.sel) {
                    const targetItems = activatingWindow ? [{
                        props: Object.assign({
                            windowUrl: activatingWindow.url ? `${activatingWindow.url}` : null,
                            windowClass: activatingWindow.constructor.name,
                            windowId: activatingWindow.id
                        }, activatingWindow.props), wnd: activatingWindow
                    }] : [];
                    menu.unshift(...await UiContext.instance.sel.buildContextMenu('wndctx', targetItems, e, 'WINDOW'));
                }
                if (menu.length != 0) {
                    menu.push({text: "-",});
                }
                menu.push({text: "window switcher:",});
                let index = 0;
                menu.push(...Object.entries(DomWindow.windows).map(([id, wnd]) => ({
                    text: ellipsize(wnd.title, 250), secondaryText: id, action() {
                        wnd.show();
                    }, [SearchableMenu.EXTRA_CLASSES]: wnd === DomWindow.current ? ['bold'] : undefined,
                    hotkey: '[' + String.fromCharCode(0x40 + ++index) + ']',
                })));
                return menu;
            },
        };
    }
    static async showWindowSwitcher(activatingWindow?: DomWindow, e?: MouseEvent) {
        await SearchableMenu.show([DomWindow.getWindowSwitcherMenuSource(activatingWindow, e)], {initialSearch: "", noFocusOnMobile:true, event: e});
    }

    /**
     * opens the specified URL in a viewer. which viewer is used is chosen on the following criteria:
     * 1. if there is a viewer which currently shows the same page (URL with equal origin, pathname and
     *    query, but potentially different hash), that viewer is used
     * 2. if options.preferredTarget is set to a viewer, that viewer is used (except if it throws a GoAwayError
     *    during navigation)
     * 3. if options.id is set to the window ID of an existing DOM window which can navigate to the given URL
     *    (either it is an ActivityWindow, or the URL protocol matches), that window is navigated
     * 4. otherwise, a new ActivityWindow is opened and navigated
     * @param url
     * @param options
     */
    static async navigate(url: URL | string, options: NavigationOptions): Promise<ActivityHolder> {
        if (typeof url === 'string') url = new URL(url);
        else if (!(url instanceof URL)) throw new Error("DomWindow.navigate expects URL");
        for(const ah of ActivityHolder.instances) {
            if (ah.url && isSamePageUrl(ah.url, url)) {
                (ah.parent as any)?.show?.();
                await ah.parent.navigate(url, options);
                return ah;
            }
        }
        if (options.preferredTarget) {
            try {
                (options.preferredTarget.parent as any).show?.();
                await options.preferredTarget.parent.navigate(url, options);
                return options.preferredTarget;
            } catch(ex) {
                if (!(ex instanceof GoAwayError)) throw ex;
            }
        }
        if (options.id) {
            const wnd = DomWindow.windows[options.id];
            if (wnd instanceof ActivityWindow || (wnd?.url && wnd.url.protocol === url.protocol)) {
                wnd.show();
                await wnd.navigate(url);
                return (wnd as any).activityHolder;
            }
        }
        const wnd = new ActivityWindow(options.id, options.defaultBounds);
        wnd.show();
        await wnd.navigate(url);
        return wnd.activityHolder;
    }

    constructor(public readonly id?: string, defaultBounds?:DomWindowBounds) {
        this.context = UiContext.instance;
        if (!this.id) this.id = getNextId();
        if (!defaultBounds) defaultBounds = {x:100,y:100,xx:500,yy:500};
        if (this.id in DomWindow.windows) {
            DomWindow.windows[this.id].show();
            throw `DomWindow with id ${this.id} already exists`;
        }
        DomWindow.windows[this.id] = this;
        this.container = EL('div', { class: 'windowOuterDiv expanded show wndStack', tabindex: '0' },
            this.titleEl = EL('div', { class: 'windowTitleBar windowDragHandle' }, this.titleSpacerEl = EL('span', {}, this.titleTextEl = EL('b', {}))),
            this.contentEl = EL('div', { class: 'windowContent' }),
            this.footerEl = EL('div', { class: 'windowFooter' }),
        );
        this.container.classList.add("wnd-"+this.id);
        this.container.classList.add("cls-"+this.constructor.name);
        this.container.setAttribute('data-domwindow', this.id);
        this.dtor.defer(() => {
            if (this.id && this.id.startsWith("tmpwnd")) localStorage.removeItem("winPos_"+this.id);
            else if (this.id) this.hide();
            this.container?.remove();
            if (DomWindow.current === this) DomWindow.current = null;
            this.container = null;
            delete DomWindow.windows[this.id];
            DomWindow.windowClosed.emit(this);
            DomWindow.hideMobileHelperIfEmpty();
        });
    
        if (DomWindowConfig.isMobileMode) {
            if (this.mobileFullscreen()) {
                document.body.appendChild(this.container);
                this.container.classList.add("mobileFullscreen");
            } else {
                DomWindow.initMobileHelper();
                DomWindow.mobileContainer.appendChild(this.container);
                if (this.allowCollapse()) this.addTitleButton("+/—", ()=> this.toggle());
                Object.values(DomWindow.windows).forEach(w => w !== this && w.collapse());
            }

            dragdrop1d(this.titleEl, 'x', [{
                button: 1, condition: (o, s) => true, init: (v) => { /*this.container.style.position = 'absolute'; */return 0; },
                transform: (newval, d) => this.container.style.transform = 'translate(' + newval + 'px, 0)',
                endMove: () => { this.container.style.transform = '';/* this.container.style.position = ''; */},
                finishMove: (newval, d) => {
                    if (newval < -20) {
                        this.setMobileFullscreen(true);
                    } else if (newval > 20) {
                        this.setMobileFullscreen(false);
                    }
                }, allowDefault: false
            }]);
        } else {
            document.body.appendChild(this.container);
            //this.$wnd.css("z-index", domWindowZIndex);
            this.$wnd.draggable({
                "handle":".windowDragHandle",
                start:()=>{ this.bringToFront(); DomWindow.coverContents(); this.focus(); },
                stop:()=>{ this.saveWinPos(); DomWindow.uncoverContents(); this.afterMove(); this.focus(); },
                //containment: [0,0,99999,99999],
            }).resizable({
                handles: "all",
                start:()=>{  DomWindow.coverContents(); this.bringToFront(); this.focus(); },
                stop:()=>{ this.saveWinPos(); DomWindow.uncoverContents(); this.afterResize(); this.focus(); }
            });
            /*
            // jQuery resizable is slow as fuck, i need to build my own dragdrop2d at some point...
            // just using 2x dragdrop1d works surprisingly well, though

            dragdrop1d(EL('div', {class:'ui-resizable-handle ui-resizable-e', style:'z-index:90',appendTo:this.container}), 'x', [{
                button: 1, condition: () => true, init: (pos) => {
                    this.bringToFront(); this.focus(); DomWindow.coverContents();
                    return this.container.clientWidth;
                }, transform: (newwidth) => this.container.style.width = newwidth + 'px',
                finishMove: (newval, diff, pointerpos) => {
                    this.saveWinPos(); DomWindow.uncoverContents(); this.afterResize(); this.focus();
                }
            }]);
            dragdrop1d(EL('div', {class:'ui-resizable-handle ui-resizable-s', style:'z-index:90',appendTo:this.container}), 'y', [{
                button: 1, condition: () => true, init: (pos) => {
                    this.bringToFront(); this.focus(); DomWindow.coverContents();
                    return this.container.clientHeight;
                }, transform: (newheight) => this.container.style.height = newheight + 'px',
                finishMove: (newval, diff, pointerpos) => {
                    this.saveWinPos(); DomWindow.uncoverContents(); this.afterResize(); this.focus();
                }
            }]);
            const bottomright = EL('div', {class:'ui-resizable-handle ui-resizable-se ui-icon ui-icon-gripsmall-diagonal-se', style:'z-index:91',appendTo:this.container})
            dragdrop1d(bottomright, 'x', [{
                button: 1, condition: () => true, init: (pos) => this.container.clientWidth, transform: (newwidth) => this.container.style.width = newwidth + 'px',
            }]);
            dragdrop1d(bottomright, 'y', [{
                button: 1, condition: () => true, init: (pos) => this.container.clientHeight, transform: (newheight) => this.container.style.height = newheight + 'px',
            }]);
            */

            this.titleEl.addEventListener('contextmenu', (e) => {
                this.toggle();
                e.preventDefault();
            });
            this.container.addEventListener("mousedown", () => {
                this.bringToFront();
                DomWindow.current = this;
            }, true);
            this.container.addEventListener("keydown", this.handleKeyEvent.bind(this), false);
        }
        this.closeButton = this.addTitleButton("X", ()=> this.close(), true, 'Escape');
        this.closeButton.setAttribute('aria-label', 'Close');
        this.closeButton.addEventListener('contextmenu', (e) => {
            e.preventDefault(); e.stopPropagation();
            DomWindow.showWindowSwitcher(this, e);
        })
        this.hotkeys['ctrl-*'] = () => {
            this.windowZoom += 0.1;
        };
        this.hotkeys['ctrl-_'] = () => {
            this.windowZoom -= 0.1;
        };
        this.hotkeys['ctrl-='] = () => {
            this.windowZoom = 1;
        };
        try{
            var pos=JSON.parse(localStorage.getItem("winPos_"+this.id));
            this.setBounds(pos);
            if (pos.hidden) this.container.style.display = 'none';
            if (pos.collapsed) this.collapse();
        } catch(ex){ this.setBounds(defaultBounds); }
        this.setTitle(`(${this.id})`);
        DomWindow.windowCreated.emit(this);
    }

    get parent(): IHierarchy { return null }
    get children(): IHierarchy[] { return []; }

    /**
     * called at first navigate
     */
    async initUI() { this._uiInitialized = true; }

    /**
     * load contents of this window by URL. instead of overriding this in subclass,
     * better override onNavigate
     *
     * the scheme of the URL must be equal to the window class
     *
     * @param url
     */
    async navigate(url: URL, navigationOptions?: NavigationOptions) {
        const testProtocol = url.protocol.toLowerCase().substring(0, url.protocol.length - 1);
        if (testProtocol !== this.constructor.name.toLowerCase() && testProtocol !== (this.constructor as typeof DomWindow).protocol?.toLowerCase()) {
            throw new Error(this.constructor.name + ".navigate called with invalid URL: " + url.toString());
        }
        if (!this._uiInitialized) {
            await this.initUI();
            if (!this._uiInitialized) throw new Error("initUI didn't set uiInitialized");
        }
        if (this.title === `(${this.id})` || this.title === this.url?.toString()) this.setTitle(url.toString());
        const prev = this.url;
        this.url = url;
        this.saveWinPos();
        const onNavigateOptions = Object.assign({}, navigationOptions, {previousUrl: prev});
        await this.onNavigate(url, onNavigateOptions);
    }

    /**
     * override in subclass to load contents of this window by URL.
     * is called in navigate(url) base implementation
     * @param url
     * @param previousUrl
     */
    protected async onNavigate(url: URL, navigationOptions: OnNavigateOptions) {
    }

    static coverContents() {
        if (!DomWindow.coverEl) DomWindow.coverEl = EL('div', {style:{position:'fixed',width:'100%',height:'100%',top:'0',left:'0',zIndex:'99999999',background:'rgba(255,200,255,0.2)'},prependTo:document.body});
        else show(DomWindow.coverEl);
    }
    static uncoverContents() {
        if (DomWindow.coverEl) { hide(DomWindow.coverEl); }
    }
    afterMove() {}
    afterResize() {}
    slot(object: EventTarget, signalName : string, handler : EventListener, callWithArgs?: any[]): EventListener;
    slot<F extends (...args: any) => any>(object: object, signalName : string | Function, handler : F, callWithArgs?: Parameters<F>): F;
    slot(object: EventTarget | object, signalName : string | Function, handler : Function, callWithArgs?: any[]): Function { throw new Error('Method not implemented.'); }
    hook<MN extends string|number|symbol, P extends any[], R>(object: object & Record<MN, Fn<P, R>>, method: MN, handler: HookCallback<P, R>, callWithArgs?: any[]) { throw new Error('Method not implemented.'); }
    allowCollapse():boolean { return true; }

    set windowZoom(val : number) {
        this.contentEl.style.zoom = (val * 100) + '%';
        this._windowZoom = val;
    }
    get windowZoom() : number {
        return this._windowZoom;
    }

    /**
     * whether the window defaults to full screen mode on mobile
     *
     * base implementation always returns true, override to change
     */
    mobileFullscreen():boolean { return true; }
    setMobileFullscreen(state: boolean) {
        if (this.isMobileFullscreen() === state) return;
        if (state) {
            document.body.appendChild(this.container);
            this.container.classList.add("mobileFullscreen");
            DomWindow.hideMobileHelperIfEmpty();
        } else {
            DomWindow.initMobileHelper(); DomWindow.showMobileHelper();
            DomWindow.mobileContainer.appendChild(this.container);
            this.container.classList.remove("mobileFullscreen");
        }
    }
    isMobileFullscreen(): boolean {
        return this.container.classList.contains("mobileFullscreen");
    }
    toggle() {
        if (this.collapsed)  this.expand();
        else  this.collapse();
    }
    collapse() {
        if (DomWindowConfig.isMobileMode && this.mobileFullscreen()) this.hide();
        if (this.collapsed || !this.allowCollapse()) return;
        this.origHeight = this.$wnd.height();
        this.container.classList.remove('expanded');
        this.container.classList.add('collapsed');
        this.container.style.height = 'auto';
        if (!DomWindowConfig.isMobileMode) this.$wnd.resizable("disable");
        this.collapsed = true;
        this.saveWinPos();
        this.afterResize();
    }
    expand() {
        if (this.collapsed) {
            this.container.style.height = Math.max(80, this.origHeight)+"px";
            this.container.classList.remove('collapsed');
            this.container.classList.add("expanded");
            if (!DomWindowConfig.isMobileMode) this.$wnd.resizable("enable");
            this.collapsed=false;
            this.saveWinPos();
            this.afterResize();
        }
        this.focus();
    }
    saveWinPos() {
        if (this.id && (!this.id.startsWith("tmpwnd") || this.url))
            localStorage.setItem("winPos_"+this.id, JSON.stringify(this.getBounds()));
    }
    bringToFront() {
        if (!DomWindowConfig.isMobileMode || this.mobileFullscreen()) bringToFrontWndStack(this.container);
    }
    getBounds() : DomWindowBounds | MobileModeBounds {
        if (DomWindowConfig.isMobileMode) {
            return { collapsed: this.collapsed, hidden: this.hidden, url: this.url?.toString(), };
        } else {
            return { x: parseInt(this.container.style.left), y: parseInt(this.container.style.top), collapsed:this.collapsed,
                    xx: parseInt(this.container.style.width), yy: this.collapsed?this.origHeight:parseInt(this.container.style.height),
                    hidden: this.hidden, url: this.url?.toString(), };
        }
    }
    setBounds(pos:DomWindowBounds) {
        if (!DomWindowConfig.isMobileMode) {
            pos.x = Math.max(0, Math.min(window.innerWidth - pos.xx, pos.x));
            pos.y = Math.max(0, Math.min(window.innerHeight - pos.yy, pos.y));
            this.$wnd.css({left:pos.x+"px",top:pos.y+"px",width:pos.xx+"px",height:Math.max(40,pos.yy)+"px"});
        }
    }
    setBoundsForce(pos:DomWindowBounds) {
        if (!DomWindowConfig.isMobileMode) {
            this.$wnd.css({left:pos.x+"px",top:pos.y+"px",width:pos.xx+"px",height:Math.max(40,pos.yy)+"px"});
        }
    }
    show() {
        this.$wnd.show();
        this.container.classList.add('show');
        this.expand();
        this.bringToFront();
        this.saveWinPos();
        if (DomWindowConfig.isMobileMode && !this.isMobileFullscreen())
            DomWindow.showMobileHelper();
    }
    hide() {
        this.$wnd.hide();
        this.container.classList.remove('show');
        this.saveWinPos();
        this.afterResize();
        DomWindow.hideMobileHelperIfEmpty();
        if (DomWindow.current == this) DomWindow.current = null;
    }
    focus() {
        this.container.focus();
        DomWindow.current = this;
    }
    close() {
        if (this.hideOnClose)
            this.hide();
        else
            this[Symbol.dispose]();
    }
    setTitle(txt:string, visible=true) {
        this.titleTextEl.replaceChildren(visible ? preprocessText(txt) : "");
        this.titleTextEl.setAttribute('title', txt);
        this.title = txt;
    }
    buttonsLeft: HTMLButtonElement[] = [];
    buttonsRight: HTMLButtonElement[] = [];
    buttonsMenu: HTMLButtonElement[] = [];
    addTitleButton(txt: string|{title: string, icon: string}, click: EventListener, right: boolean = false, hotkey?: string): HTMLButtonElement {
        const btn = DomWindow.makeButton(txt, click, right, hotkey);
        this.addTitleButtonEx(btn);
        return btn;
    }
    addTitleButtonEx(btn: HTMLButtonElement) {
        this.registerButton(btn);
        this.updateButtons();
        return btn;
    }
    private registerButton(btn: HTMLButtonElement) {
        if (btn.hasAttribute('data-hotkey')) this.buttonHotkeys[btn.getAttribute('data-hotkey')] = btn;
        if (btn.getAttribute('data-side') === 'right') this.buttonsRight.unshift(btn);
        else if (btn.getAttribute('data-side') === 'left') this.buttonsLeft.push(btn);
        else if (btn.getAttribute('data-side') === 'menu') this.buttonsMenu.push(btn);
    }
    static makeButton(txt: string|{title: string, icon: string}, click: EventListener, position: boolean | string = 'left', hotkey?: string): HTMLButtonElement {
        var btn = EL("button", { onclick: click, 'data-hotkey': hotkey, 'data-side': position === true ? 'right' : position === false ? 'left' : position });
        if (typeof txt === 'object') {
            renderIcon2(txt.icon, 16, null, btn);
            btn.setAttribute('title', txt.title);
        } else btn.innerText = txt;
        if (hotkey) { btn.title += ' ['+hotkey+']'; btn.setAttribute('data-hotkey', hotkey); }
        return btn;
    }
    setButtons(buttons: HTMLButtonElement[]) {
        this.buttonHotkeys = {};
        this.buttonsLeft = [];
        this.buttonsRight = [];
        this.buttonsMenu = [];
        for(const btn of [this.closeButton].concat(buttons))
            this.registerButton(btn);

        this.updateButtons();
    }
    getButtons(): HTMLButtonElement[] {
        return this.buttonsLeft.concat(this.buttonsRight.toReversed().slice(1), this.buttonsMenu);
    }
    updateButtons() {
        this.titleEl.replaceChildren(...this.buttonsLeft, this.titleSpacerEl, ...this.buttonsRight);
    }

    static forElement(el : HTMLElement): DomWindow {
        if (el.hasAttribute('data-domwindow')) {
            var id = el.getAttribute('data-domwindow');
            if (id in DomWindow.windows) return DomWindow.windows[id];
        }
        throw "No DOM window for element";
    }

    static initMobileHelper() {
        if (DomWindow.mobileContainer) return;
        var bg = document.createElement("div");
        bg.id = "mobileDomWindowContainer";
        bg.classList.add('wndStack');
        DomWindow.mobileContainer = document.createElement("div");
        bg.appendChild(DomWindow.mobileContainer);
        document.body.appendChild(bg);
        bg.style.display="none";
        $(bg).click(function(e){
            if(e.target != this) return;
            bg.style.display="none";
        });
    }
    static showMobileHelper() {
        if (DomWindow.mobileContainer){
            DomWindow.mobileContainer.parentElement.style.display="block";
            bringToFrontWndStack(DomWindow.mobileContainer.parentElement);
        }
    }
    static hideMobileHelperIfEmpty() {
        if (DomWindow.mobileContainer && Array.prototype.every.call(DomWindow.mobileContainer.children, (wndEl) => !isVisible(wndEl))) {
            DomWindow.mobileContainer.parentElement.style.display="none";
        }
    }
    handleKeyEvent(e: KeyboardEvent | string) {
        const keyName = typeof e === 'string' ? e : keyname(e);
        if (e instanceof KeyboardEvent && e.defaultPrevented) return;
        if (keyName === 'alt--') {
            this.collapse();
        } else if (keyName === 'alt-+') {
            this.expand();
        } else if (keyName === 'alt-Dead') {
            DomWindow.showWindowSwitcher(this, null);
        } else if (keyName in this.hotkeys) {
            if (typeof e === 'object') {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
            }
            const hotkey = this.hotkeys[keyName];
            hotkey();
        } else if (keyName in this.buttonHotkeys) {
            if (typeof e === 'object') {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
            }
            const hotkey = this.buttonHotkeys[keyName];
            if (hotkey.disabled) return;
            hotkey.click();
        }
    }
}

export class ActivityWindow extends DomWindow implements IActivityHost {
    readonly activityHolder = this.dtor.use(new ActivityHolder(this));

    urlBar: HTMLElement;
    urlBarInput: HTMLInputElement;
    progressBar: HTMLElement;
    history: URL[] = [];
    _historyIndex: number = 0;
    set historyIndex(val: number) {
        this._historyIndex = val;
        this.backButton.disabled = val === 0;
        this.forwardButton.disabled = val === this.history.length - 1;
    }
    get historyIndex() { return this._historyIndex }
    backButton: HTMLButtonElement;
    forwardButton: HTMLButtonElement;
    abortController: AbortController;

    private systemButtons;
    constructor(id?: string, defaultBounds?:DomWindowBounds) {
        super(id, defaultBounds);

        this.dtor.defer(() => this.abortController?.abort());

        this.progressBar = EL('div', { class: 'progressBar local', appendTo: this.contentEl });
        this.urlBar = EL('form', {
                onsubmit: (e) => {e.preventDefault(); this.navigate(new URL(this.urlBarInput.value));},
                onkeydown: (e: KeyboardEvent) => {
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        if (this.urlBarInput.value !== this.url.toString()) {
                            this.urlBarInput.value = this.url.toString(); this.urlBarInput.focus(); this.urlBarInput.select();
                        } else {
                            hide(this.urlBar);
                            this.container.focus();
                        }
                    }
                },
                appendTo: this.contentEl, class:'windowUrlBar', style:{display:'flex'}, hidden: 'hidden'
            },
            this.backButton = EL('button', {type: 'button', title:'Go back', disabled: "disabled", onclick: () => this.goHistory(-1)}, '←'),
            this.forwardButton = EL('button', {type: 'button', title:'Go forward', disabled: "disabled", onclick: () => this.goHistory(1)}, '→'),
            EL('button', {type: 'button', title:'Refresh', onclick: () => this.reload()}, '⭮'),
            this.urlBarInput = EL('input', {type:'url', style:{flex:'1'}}),
            EL('button', {type:'submit', title:'Go'}, '»'));
        this.systemButtons = [
            DomWindow.makeButton('Show URL Bar', () => this.showUrlBar(), 'menu', 'ctrl-l'),
            DomWindow.makeButton('Go Back', () => this.goHistory(-1), 'menu', 'alt-ArrowLeft'),
            DomWindow.makeButton('Go Forward', () => this.goHistory(1), 'menu', 'alt-ArrowRight'),
        ];
        super.setButtons(this.systemButtons);
        this.contentEl.style.display = 'flex';
        this.contentEl.style.flexDirection = 'column';
        this.contentEl.style.overflow = 'hidden';
        this.hotkeys['Escape'] = () => {
            if (this.historyIndex === 0) {
                this.close();
            } else {
                this.goHistory(-1);
            }
        };
        this.hotkeys['shift-Escape'] = () => {
            this.goHistory(1);
        }
        this.slot(this.activityHolder, 'isPinned$', (bOld, bNew) => {
            this.urlBarInput.readOnly = bNew;
        });
    }

    get children(): IHierarchy[] { return [this.activityHolder.activity]; }

    showUrlBar(focus = true) {
        show(this.urlBar); if (focus) this.urlBarInput.focus(); this.urlBarInput.select();
    }

    setContent(content: HTMLElement) {
        this.contentEl.replaceChildren(this.progressBar, this.urlBar, content);
    }
    setButtons(buttons: HTMLButtonElement[]) {
        super.setButtons(this.systemButtons.concat(buttons));
    }

    async goHistory(direction: number) {
        const newIndex = clamp(this.historyIndex + direction, 0, this.history.length - 1);
        await this.onNavigate(this.history[newIndex], {previousUrl: this.url, userAction: 'history'});
        this.historyIndex = newIndex;
    }
    async reload() {
        await this.onNavigate(this.url, {previousUrl: this.url, refresh: true, userAction: 'history'});
    }
    async navigate(url: URL, navigationOptions?: NavigationOptions) {
        const prev = this.url;
        const options = Object.assign({}, navigationOptions, {previousUrl: prev});
        await this.onNavigate(url, options);
        if (url && (this.history.length === 0 || this.history[this.historyIndex].toString() !== url.toString()))
            this.history.splice(this.historyIndex + 1, this.history.length - this.historyIndex - 1, url);
        this.historyIndex = this.history.length - 1;
    }
    private setLoading(loading: boolean) {
        this.progressBar.classList.toggle('visible', loading);
        if (this.abortController) this.abortController.abort();
        this.abortController = loading ? new AbortController() : null;
    }
    protected async onNavigate(url: URL, options: OnNavigateOptions) {
        try {
            this.setLoading(true);
            await this.activityHolder.onBeforeNavigate(url, options);

            this.url = url;
            this.saveWinPos();

            this.urlBarInput.value = url.toString();

            options.cancel = this.abortController.signal;
            await this.activityHolder.onNavigate(url, options);
        } finally {
            this.setLoading(false);
        }
    }
    afterResize() {
        super.afterResize();
        this.activityHolder.activity?.afterResize();
    }
}
